import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '../../design-system/theme/reviewDesk';
import { useTranslation } from '../../hooks/useTranslation';
import { Cohort, cohortsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import McsCohortSummaryPanel from '../mcs/McsCohortSummaryPanel';
import { MCS_ENABLED } from '../../config/featureFlags';
import { apiRequest } from '../../services/api/apiClient';

interface Vocabulary {
  program_levels: string[];
  feature_keys: string[];
}

interface CohortSettingsModalProps {
  visible: boolean;
  cohort: Cohort | null;
  schoolId: number | null;
  onClose: () => void;
  onSaved: (updated: Cohort) => void;
}

// Pretty labels keyed by API enum value. Falls back to the raw value if missing.
const PROGRAM_LEVEL_LABELS: Record<string, { en: string; tr: string }> = {
  icf_level_1_acc: { en: 'ICF Level 1 (ACC)', tr: 'ICF Seviye 1 (ACC)' },
  icf_level_2_pcc: { en: 'ICF Level 2 (PCC)', tr: 'ICF Seviye 2 (PCC)' },
  icf_level_3_mcc: { en: 'ICF Level 3 (MCC)', tr: 'ICF Seviye 3 (MCC)' },
  icf_mcs: { en: 'ICF Mentor Coach Specialization', tr: 'ICF Mentor Koç Uzmanlığı' },
  emcc_foundation: { en: 'EMCC Foundation', tr: 'EMCC Temel' },
  emcc_practitioner: { en: 'EMCC Practitioner', tr: 'EMCC Uygulayıcı' },
  emcc_senior: { en: 'EMCC Senior Practitioner', tr: 'EMCC Kıdemli Uygulayıcı' },
  emcc_master: { en: 'EMCC Master Practitioner', tr: 'EMCC Usta Uygulayıcı' },
  ac_foundation: { en: 'AC Foundation Coach', tr: 'AC Temel Koç' },
  ac_coach: { en: 'AC Coach', tr: 'AC Koç' },
  ac_professional: { en: 'AC Professional Coach', tr: 'AC Profesyonel Koç' },
  ac_master: { en: 'AC Master Coach', tr: 'AC Usta Koç' },
  cce: { en: 'Continuing Coach Education (CCE)', tr: 'Sürekli Koçluk Eğitimi (CCE)' },
  custom: { en: 'Custom', tr: 'Özel' },
};

const FEATURE_LABELS: Record<string, { en: string; tr: string; description: { en: string; tr: string } }> = {
  mentor_training: {
    en: 'Mentor Coach Training',
    tr: 'Mentor Koç Eğitimi',
    description: {
      en: 'Enables MCQ-style drills where students grade analyzed transcripts as mentor coaches.',
      tr: 'Öğrencilerin analiz edilmiş transkriptleri mentor koç olarak puanladığı MCQ tarzı alıştırmaları etkinleştirir.',
    },
  },
};

export default function CohortSettingsModal({
  visible,
  cohort,
  schoolId,
  onClose,
  onSaved,
}: CohortSettingsModalProps) {
  const { t, language } = useTranslation();
  const { user } = useAuth();
  const lang: 'en' | 'tr' = language === 'tr' ? 'tr' : 'en';

  const [vocabulary, setVocabulary] = useState<Vocabulary | null>(null);
  const [programLevel, setProgramLevel] = useState<string>('');
  const [features, setFeatures] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !cohort) return;
    setProgramLevel(cohort.program_level || '');
    setFeatures([...(cohort.enabled_features || [])]);
    setError(null);
  }, [visible, cohort]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiRequest<Vocabulary>({
          endpoint: '/cohorts/vocabulary',
          method: 'GET',
        });
        if (!cancelled) setVocabulary(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  const toggleFeature = (key: string) => {
    setFeatures((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const onSave = async () => {
    if (!cohort || !schoolId) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await cohortsAPI.update(schoolId, cohort.id, {
        program_level: programLevel || null,
        enabled_features: features,
      });
      onSaved(updated);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const labelFor = (key: string): string => {
    const entry = PROGRAM_LEVEL_LABELS[key];
    if (!entry) return key;
    return entry[lang];
  };

  const featureLabel = (key: string) => FEATURE_LABELS[key]?.[lang] ?? key;
  const featureDescription = (key: string) => FEATURE_LABELS[key]?.description[lang] ?? '';

  if (!cohort) return null;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.modal}>
          <View style={styles.header}>
            <View style={{ flex: 1 }}>
              <Text style={styles.title}>
                {t('cohortSettings.title')}
              </Text>
              <Text style={styles.subtitle}>{cohort.name}</Text>
            </View>
            <TouchableOpacity onPress={onClose}>
              <MaterialIcons name="close" size={20} color={reviewDesk.muted} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
            <View style={styles.field}>
              <Text style={styles.label}>
                {t('cohortSettings.programLevel')}
              </Text>
              <Text style={styles.hint}>
                {t(
                  'cohortSettings.programLevelHint'
                )}
              </Text>
              {!vocabulary ? (
                <ActivityIndicator size="small" color={reviewDesk.accent} />
              ) : (
                <View style={styles.levelGrid}>
                  <Pressable
                    onPress={() => setProgramLevel('')}
                    style={({ hovered }: any) => [
                      styles.levelOption,
                      hovered && { borderColor: reviewDesk.lineStrong },
                      programLevel === '' && styles.levelOptionActive,
                    ]}
                  >
                    <Text style={[styles.levelOptionText, programLevel === '' && styles.levelOptionTextActive]}>
                      —{' '}
                      {t('cohortSettings.noLevel')}
                    </Text>
                  </Pressable>
                  {vocabulary.program_levels.map((key) => {
                    const active = programLevel === key;
                    return (
                      <Pressable
                        key={key}
                        onPress={() => setProgramLevel(key)}
                        style={({ hovered }: any) => [
                          styles.levelOption,
                          hovered && { borderColor: reviewDesk.lineStrong },
                          active && styles.levelOptionActive,
                        ]}
                      >
                        <Text style={[styles.levelOptionText, active && styles.levelOptionTextActive]}>
                          {labelFor(key)}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              )}
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>
                {t('cohortSettings.features')}
              </Text>
              <Text style={styles.hint}>
                {t(
                  'cohortSettings.featuresHint'
                )}
              </Text>
              {!vocabulary ? (
                <ActivityIndicator size="small" color={reviewDesk.accent} />
              ) : vocabulary.feature_keys.length === 0 ? (
                <Text style={styles.hint}>{t('cohortSettings.noFeatures')}</Text>
              ) : (
                <View style={{ gap: 8 }}>
                  {vocabulary.feature_keys.map((key) => {
                    const enabled = features.includes(key);
                    return (
                      <Pressable
                        key={key}
                        onPress={() => toggleFeature(key)}
                        style={({ hovered }: any) => [
                          styles.featureRow,
                          hovered && { borderColor: reviewDesk.lineStrong },
                          enabled && styles.featureRowOn,
                        ]}
                      >
                        <View style={[styles.toggle, enabled && styles.toggleOn]}>
                          <View style={[styles.toggleDot, enabled && styles.toggleDotOn]} />
                        </View>
                        <View style={styles.featureBody}>
                          <Text style={styles.featureName}>{featureLabel(key)}</Text>
                          {!!featureDescription(key) && (
                            <Text style={styles.featureDesc}>{featureDescription(key)}</Text>
                          )}
                        </View>
                      </Pressable>
                    );
                  })}
                </View>
              )}
            </View>

            {MCS_ENABLED && visible && user?.user_type === 'b2b2c_admin' && cohort.program_level === 'icf_mcs' && <McsCohortSummaryPanel cohortId={cohort.id} />}
            {!!error && (
              <View style={styles.errorBox}>
                <Text style={styles.errorText}>{error}</Text>
              </View>
            )}
          </ScrollView>

          <View style={styles.footer}>
            <TouchableOpacity onPress={onClose} style={styles.cancelBtn}>
              <Text style={styles.cancelBtnText}>
                {t('cohortSettings.cancel')}
              </Text>
            </TouchableOpacity>
            <Pressable
              onPress={onSave}
              disabled={saving}
              style={({ hovered }: any) => [
                styles.saveBtn,
                saving && { opacity: 0.5 },
                hovered && !saving && { backgroundColor: '#1F36A0' },
              ]}
            >
              {saving ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.saveBtnText}>
                  {t('cohortSettings.save')}
                </Text>
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(16, 24, 39, 0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modal: {
    width: '100%',
    maxWidth: 640,
    maxHeight: '90%',
    backgroundColor: reviewDesk.card,
    borderRadius: 14,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 22,
    paddingVertical: 16,
    borderBottomColor: reviewDesk.line,
    borderBottomWidth: 1,
  },
  title: { fontSize: 17, fontWeight: '700', color: reviewDesk.ink },
  subtitle: { fontSize: 13, color: reviewDesk.muted, marginTop: 4 },
  body: { flexGrow: 1 },
  bodyContent: { padding: 22, gap: 22 },
  field: { gap: 8 },
  label: { fontSize: 13, fontWeight: '600', color: reviewDesk.inkSoft },
  hint: { fontSize: 12, color: reviewDesk.muted, lineHeight: 18 },
  levelGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  levelOption: {
    paddingHorizontal: 11,
    paddingVertical: 7,
    backgroundColor: reviewDesk.cardMuted,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 8,
  },
  levelOptionActive: { backgroundColor: reviewDesk.accent, borderColor: reviewDesk.accent },
  levelOptionText: { fontSize: 12, fontWeight: '500', color: reviewDesk.inkSoft },
  levelOptionTextActive: { color: '#fff', fontWeight: '600' },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: reviewDesk.cardMuted,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 10,
  },
  featureRowOn: { backgroundColor: reviewDesk.accentSoft, borderColor: reviewDesk.accent },
  toggle: {
    width: 36,
    height: 20,
    borderRadius: 10,
    backgroundColor: reviewDesk.lineStrong,
    padding: 2,
    justifyContent: 'center',
  },
  toggleOn: { backgroundColor: reviewDesk.accent },
  toggleDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#fff',
    alignSelf: 'flex-start',
  },
  toggleDotOn: { alignSelf: 'flex-end' },
  featureBody: { flex: 1, gap: 3 },
  featureName: { fontSize: 13, fontWeight: '600', color: reviewDesk.ink },
  featureDesc: { fontSize: 12, color: reviewDesk.inkSoft, lineHeight: 18 },
  errorBox: { padding: 10, backgroundColor: '#FFE7E1', borderRadius: 6 },
  errorText: { color: '#7A2B1F', fontSize: 12 },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    paddingHorizontal: 22,
    paddingVertical: 14,
    borderTopColor: reviewDesk.line,
    borderTopWidth: 1,
  },
  cancelBtn: { paddingHorizontal: 14, paddingVertical: 9 },
  cancelBtnText: { fontSize: 13, color: reviewDesk.inkSoft, fontWeight: '500' },
  saveBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: reviewDesk.accent,
    borderRadius: 8,
    minWidth: 120,
    alignItems: 'center',
  },
  saveBtnText: { color: '#fff', fontWeight: '600', fontSize: 13 },
});
